import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  AlertTriangle,
  ArrowLeft,
  Check,
  CheckCheck,
  ChevronDown,
  GitCompare,
  Download,
  FileText,
  Loader2,
  Pencil,
  RotateCcw,
  X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useI18n } from "@/i18n";
import {
  acceptAllFindings,
  Comparison,
  ComparisonFinding,
  exportComparison,
  getComparison,
  startComparison,
  updateFinding,
} from "../../services/Comparisons";
import { OVERALL, RISK_CHIP } from "./risk-styles";
import ContinueInChatButton from "./ContinueInChatButton";
import { startDownload } from "../../utils/download";

type ExportFormat = "redline" | "clean" | "report";
type Filter = "all" | "high" | "medium" | "low" | "missing" | "added" | "open";
const FILTERS: Filter[] = ["all", "high", "medium", "low", "missing", "added", "open"];

const humanTopic = (topic: string | null) => (topic ? topic.replace(/_/g, " ") : null);

/** One received agreement compared with a standard: summary, findings, exports */
const ComparisonPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { t, locale } = useI18n();
  const c = t.comparisons;
  const navigate = useNavigate();
  const [data, setData] = useState<Comparison | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [filter, setFilter] = useState<Filter>("all");
  const [busy, setBusy] = useState<string | null>(null);
  const [editing, setEditing] = useState<{ id: string; text: string } | null>(null);
  const [showMatches, setShowMatches] = useState(false);
  const [confirmRerun, setConfirmRerun] = useState(false);

  const load = useCallback(async () => {
    if (!id) return;
    const res = await getComparison(id);
    if (res.success && res.data) {
      setData(res.data);
      setLoadError(false);
    } else {
      setLoadError(true);
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  // Poll while the comparison runs
  const running = data?.status === "queued" || data?.status === "processing";
  useEffect(() => {
    if (!running) return;
    const timer = setInterval(() => void load(), 2000);
    return () => clearInterval(timer);
  }, [running, load]);

  const deviations = useMemo(() => (data?.findings ?? []).filter((f) => f.classification !== "match"), [data]);
  const matches = useMemo(() => (data?.findings ?? []).filter((f) => f.classification === "match"), [data]);
  const shown = deviations.filter((f) => {
    switch (filter) {
      case "high":
      case "medium":
      case "low":
        return f.risk === filter;
      case "missing":
      case "added":
        return f.classification === filter;
      case "open":
        return f.status === "open";
      default:
        return true;
    }
  });
  const acceptedCount = deviations.filter((f) => f.status === "accepted" && f.suggestion).length;

  const act = async (
    key: string,
    fn: () => Promise<{ success: boolean; data?: Comparison | null; errors?: Array<{ title?: string; detail?: string }> }>,
  ) => {
    setBusy(key);
    try {
      const res = await fn();
      if (res.success && res.data) setData(res.data);
      else toast.error(c.updateFailed);
      return res.success;
    } finally {
      setBusy(null);
    }
  };

  const setStatus = (f: ComparisonFinding, status: ComparisonFinding["status"]) =>
    act(f.id, () => updateFinding(data!.id, f.id, { status }));

  const saveWording = async (f: ComparisonFinding) => {
    if (!editing) return;
    const ok = await act(f.id, () => updateFinding(data!.id, f.id, { status: "accepted", newText: editing.text }));
    if (ok) setEditing(null);
  };

  const download = async (format: ExportFormat) => {
    if (!data) return;
    setBusy(format);
    try {
      const res = await exportComparison(data.id, format);
      if (res.success && res.data) {
        startDownload(res.data.downloadUrl, res.data.fileName);
        toast.success(c.exported(res.data.fileName));
        if (res.data.skipped) toast.warning(c.skipped(res.data.skipped));
      } else {
        toast.error(c.exportFailed);
      }
    } finally {
      setBusy(null);
    }
  };

  const rerun = async () => {
    if (!data) return;
    setConfirmRerun(false);
    setBusy("rerun");
    try {
      const res = await startComparison({
        standardAgreementId: data.standardAgreementId,
        receivedFileId: data.receivedFileId,
        ourRole: data.ourRole ?? undefined,
        focus: data.focus ?? undefined,
        language: locale === "sv" ? "sv" : "en",
        force: true,
      });
      if (res.success && res.data) navigate(`/comparisons/${res.data.id}`);
      else toast.error(c.startFailed);
    } finally {
      setBusy(null);
    }
  };

  if (loadError) {
    return (
      <div className="w-full p-8">
        <Link to="/compare" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:underline">
          <ArrowLeft className="h-4 w-4" />
          {c.back}
        </Link>
        <p className="mt-6 text-muted-foreground">{c.updateFailed}</p>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="flex justify-center py-16">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // Same standard, role and focus: the Compare page only needs the new document
  const compareAnotherUrl = `/compare?${new URLSearchParams({
    standard: data.standardAgreementId,
    ...(data.ourRole ? { role: data.ourRole } : {}),
    ...(data.focus ? { focus: data.focus } : {}),
  })}`;
  const progress = data.progress;
  const pct = progress && progress.total ? Math.round((progress.done / progress.total) * 100) : 0;

  return (
    <div className="mx-auto w-full max-w-5xl space-y-6 p-4 sm:p-8">
      <div>
        <Link to="/compare" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:underline">
          <ArrowLeft className="h-4 w-4" />
          {c.back}
        </Link>
        <h1 className="mt-2 break-words text-2xl font-bold tracking-tight sm:text-3xl">{data.receivedFileName}</h1>
        <p className="text-muted-foreground">
          {c.standard}: {data.standardName}
          {data.standardVersion ? ` (${c.version(data.standardVersion)})` : ""}
          {data.projectName && (
            <>
              {" · "}
              <Link to={`/qagent/project/${data.projectId}`} className="hover:underline">
                {data.projectKind === "compare" ? c.cp.mineTitle : data.projectName}
              </Link>
            </>
          )}
        </p>
      </div>

      {running && (
        <Card className="p-6">
          <div className="flex items-center gap-3">
            <Loader2 className="h-5 w-5 animate-spin text-brand" />
            <div className="font-semibold">{c.progressTitle}</div>
          </div>
          <div className="mt-4 text-sm text-muted-foreground">
            {progress ? c.stages[progress.stage] : c.statuses.queued}
            {progress?.stage === "analysing" && progress.total > 0 && ` · ${progress.done}/${progress.total}`}
          </div>
          <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-muted">
            <div
              className="h-full rounded-full bg-brand transition-all"
              style={{ width: `${progress?.stage === "analysing" ? Math.max(10, pct) : progress?.stage === "summarising" ? 95 : 5}%` }}
            />
          </div>
          <p className="mt-3 text-xs text-muted-foreground">{c.progressHint}</p>
        </Card>
      )}

      {data.status === "failed" && (
        <Card className="p-6">
          <div className="flex items-start gap-3">
            <AlertTriangle className="mt-0.5 h-5 w-5 text-red-600" />
            <div>
              <div className="font-semibold">{c.failedTitle}</div>
              <p className="mt-1 text-sm text-muted-foreground">{data.error}</p>
              {data.canEdit && (
                <Button className="mt-4" size="sm" disabled={busy === "rerun"} onClick={() => void rerun()}>
                  <RotateCcw className="mr-2 h-4 w-4" />
                  {c.retry}
                </Button>
              )}
            </div>
          </div>
        </Card>
      )}

      {data.status === "ready" && (
        <>
          <Card className="p-6">
            <div className="flex flex-col gap-4">
              <div className="min-w-0 flex-1">
                {data.summary?.overall && (
                  <div className={`text-lg font-semibold ${OVERALL[data.summary.overall]}`}>{c.overall[data.summary.overall]}</div>
                )}
                {data.summary?.headline && <p className="mt-1">{data.summary.headline}</p>}
                {data.summary?.counts && (
                  <div className="mt-3 flex flex-wrap gap-2 text-xs font-medium">
                    {(["high", "medium", "low"] as const).map((r) => (
                      <span key={r} className={`rounded-full px-2 py-0.5 ${RISK_CHIP[r]}`}>
                        {c.risks[r]} {data.summary!.counts[r]}
                      </span>
                    ))}
                    <span className={`rounded-full px-2 py-0.5 ${RISK_CHIP.none}`}>
                      {c.kinds.match} {data.summary.counts.matches}
                    </span>
                  </div>
                )}
              </div>
              <div className="flex flex-wrap gap-2">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="outline"
                      disabled={!!busy && ["redline", "clean", "report"].includes(busy)}
                      data-testid="download-menu"
                    >
                      {busy && ["redline", "clean", "report"].includes(busy) ? (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      ) : (
                        <Download className="mr-2 h-4 w-4" />
                      )}
                      {c.download}
                      <ChevronDown className="ml-2 h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="max-w-[calc(100vw-2rem)]">
                    {(["redline", "clean"] as const).map((format) => (
                      <DropdownMenuItem
                        key={format}
                        disabled={!data.redlineAvailable || !acceptedCount}
                        className="flex-col items-start gap-0.5"
                        data-testid={`download-${format}`}
                        onClick={() => void download(format)}
                      >
                        <span className="font-medium">{format === "redline" ? c.redlineShort : c.cleanShort}</span>
                        <span className="text-xs text-muted-foreground">
                          {!data.redlineAvailable
                            ? c.redlinePdf
                            : !acceptedCount
                              ? c.needAccepted
                              : format === "redline"
                                ? c.redlineHint
                                : c.cleanHint}
                        </span>
                      </DropdownMenuItem>
                    ))}
                    <DropdownMenuItem
                      className="flex-col items-start gap-0.5"
                      data-testid="download-report"
                      onClick={() => void download("report")}
                    >
                      <span className="font-medium">{c.reportShort}</span>
                      <span className="text-xs text-muted-foreground">{c.reportHint}</span>
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
                <ContinueInChatButton comparisonId={data.id} variant="outline" />
                <Button variant="outline" title={c.cp.compareAnotherHint} onClick={() => navigate(compareAnotherUrl)}>
                  <GitCompare className="mr-2 h-4 w-4" />
                  {c.cp.compareAnother}
                </Button>
              </div>
            </div>
            {!!data.summary?.incomplete && (
              <div className="mt-4 flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-200">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                <span>{c.incomplete}</span>
              </div>
            )}
            {(!!data.summary?.topDeviations?.length || !!data.summary?.nextSteps?.length) && (
              <div className="mt-5 grid gap-6 md:grid-cols-2">
                {!!data.summary?.topDeviations?.length && (
                  <div>
                    <h2 className="mb-2 text-sm font-semibold">{c.topDeviations}</h2>
                    <ol className="list-decimal space-y-1 pl-5 text-sm">
                      {data.summary.topDeviations.map((d, i) => (
                        <li key={i}>{d}</li>
                      ))}
                    </ol>
                  </div>
                )}
                {!!data.summary?.nextSteps?.length && (
                  <div>
                    <h2 className="mb-2 text-sm font-semibold">{c.nextSteps}</h2>
                    <ul className="list-disc space-y-1 pl-5 text-sm">
                      {data.summary.nextSteps.map((d, i) => (
                        <li key={i}>{d}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </Card>

          {!data.canEdit && <p className="text-sm text-muted-foreground">{c.viewOnly}</p>}

          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-wrap gap-1.5" role="tablist" aria-label={c.findingsTitle}>
              {FILTERS.map((f) => (
                <button
                  key={f}
                  type="button"
                  role="tab"
                  aria-selected={filter === f}
                  onClick={() => setFilter(f)}
                  className={`rounded-full border px-3 py-1 text-sm transition-colors ${
                    filter === f ? "border-brand bg-brand text-white" : "hover:bg-muted"
                  }`}
                >
                  {c.filters[f]}
                </button>
              ))}
            </div>
            {data.canEdit && (
              <div className="flex gap-2">
                <Button variant="ghost" size="sm" disabled={busy === "rerun"} onClick={() => setConfirmRerun(true)}>
                  <RotateCcw className="mr-1.5 h-4 w-4" />
                  {c.rerun}
                </Button>
                <Button size="sm" disabled={busy === "accept-all"} onClick={() => void act("accept-all", () => acceptAllFindings(data.id))}>
                  <CheckCheck className="mr-1.5 h-4 w-4" />
                  {c.acceptAll}
                </Button>
              </div>
            )}
          </div>

          <div className="space-y-3">
            {shown.length === 0 && <p className="text-sm text-muted-foreground">{c.noFindings}</p>}
            {shown.map((f) => (
              <Card key={f.id} className={`p-4 transition-opacity ${f.status === "dismissed" ? "opacity-60" : ""}`}>
                <div className="flex flex-wrap items-center gap-2 text-xs">
                  <span className={`rounded-full px-2 py-0.5 font-semibold ${RISK_CHIP[f.risk]}`}>{c.risks[f.risk]}</span>
                  <span className="font-medium">
                    {c.kinds[f.classification]}
                    {f.direction && f.classification === "changed" ? ` · ${c.directions[f.direction]}` : ""}
                  </span>
                  {humanTopic(f.topic) && <span className="text-muted-foreground">· {humanTopic(f.topic)}</span>}
                  <span className="text-muted-foreground">· {c.clauseRefs(f.receivedRef, f.standardRef)}</span>
                  {f.confidence === "low" && (
                    <span className="rounded-full bg-amber-50 px-2 py-0.5 text-amber-800 dark:bg-amber-950 dark:text-amber-200">
                      {c.lowConfidence}
                    </span>
                  )}
                  {f.status !== "open" && (
                    <span
                      className={`ml-auto font-medium ${f.status === "accepted" ? "text-emerald-700 dark:text-emerald-400" : "text-muted-foreground"}`}
                    >
                      {f.status === "accepted" ? c.accepted : c.dismissed}
                    </span>
                  )}
                </div>
                <div className="mt-2 font-medium">{f.summary}</div>
                {f.explanation && <p className="mt-1 text-sm text-muted-foreground">{f.explanation}</p>}
                {(f.standardQuote || f.receivedQuote) && (
                  <div className="mt-3 grid gap-2 text-sm md:grid-cols-2">
                    {f.receivedQuote && (
                      <blockquote className="rounded-md border-l-4 border-red-300 bg-muted/40 px-3 py-2">
                        <div className="mb-1 text-xs font-medium text-muted-foreground">{c.theirText}</div>
                        {f.receivedQuote}
                      </blockquote>
                    )}
                    {f.standardQuote && (
                      <blockquote className="rounded-md border-l-4 border-emerald-300 bg-muted/40 px-3 py-2">
                        <div className="mb-1 text-xs font-medium text-muted-foreground">{c.ourStandard}</div>
                        {f.standardQuote}
                      </blockquote>
                    )}
                  </div>
                )}
                {f.suggestion && (
                  <div className="mt-3 rounded-md border bg-background p-3 text-sm">
                    <div className="mb-1 text-xs font-medium text-muted-foreground">
                      {c.suggestion} · {c.suggestionKinds[f.suggestion.kind]}
                    </div>
                    {editing?.id === f.id ? (
                      <>
                        <Textarea rows={4} value={editing.text} onChange={(e) => setEditing({ id: f.id, text: e.target.value })} />
                        <div className="mt-2 flex gap-2">
                          <Button size="sm" disabled={busy === f.id || !editing.text.trim()} onClick={() => void saveWording(f)}>
                            {c.saveWording}
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => setEditing(null)}>
                            {c.cancel}
                          </Button>
                        </div>
                      </>
                    ) : f.suggestion.kind === "delete" ? (
                      <span className="text-red-700 line-through dark:text-red-400">{f.suggestion.oldText}</span>
                    ) : (
                      <>
                        {f.suggestion.kind === "edit" && f.suggestion.oldText && (
                          <div className="text-red-700 line-through dark:text-red-400">{f.suggestion.oldText}</div>
                        )}
                        <div className="text-emerald-800 dark:text-emerald-300">{f.suggestion.newText}</div>
                      </>
                    )}
                  </div>
                )}
                {data.canEdit && editing?.id !== f.id && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {f.status === "open" ? (
                      <>
                        {f.suggestion && (
                          <Button size="sm" disabled={busy === f.id} onClick={() => void setStatus(f, "accepted")}>
                            <Check className="mr-1.5 h-4 w-4" />
                            {c.accept}
                          </Button>
                        )}
                        <Button size="sm" variant="outline" disabled={busy === f.id} onClick={() => void setStatus(f, "dismissed")}>
                          <X className="mr-1.5 h-4 w-4" />
                          {c.dismiss}
                        </Button>
                      </>
                    ) : (
                      <Button size="sm" variant="outline" disabled={busy === f.id} onClick={() => void setStatus(f, "open")}>
                        <RotateCcw className="mr-1.5 h-4 w-4" />
                        {c.reopen}
                      </Button>
                    )}
                    {f.suggestion && f.suggestion.kind !== "delete" && (
                      <Button size="sm" variant="ghost" onClick={() => setEditing({ id: f.id, text: f.suggestion?.newText ?? "" })}>
                        <Pencil className="mr-1.5 h-4 w-4" />
                        {c.editWording}
                      </Button>
                    )}
                  </div>
                )}
              </Card>
            ))}
          </div>

          {matches.length > 0 && (
            <Card className="p-4">
              <button
                type="button"
                className="flex w-full items-center justify-between text-left font-medium"
                onClick={() => setShowMatches((v) => !v)}
              >
                {c.matchesTitle(matches.length)}
                <ChevronDown className={`h-4 w-4 transition-transform ${showMatches ? "rotate-180" : ""}`} />
              </button>
              {showMatches && (
                <ul className="mt-3 space-y-1 text-sm">
                  {matches.map((m) => (
                    <li key={m.id} className="flex gap-2">
                      <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />
                      <span>
                        <span className="font-medium">{c.clauseRefs(m.receivedRef, m.standardRef)}</span>
                        {m.summary && <span className="text-muted-foreground"> · {m.summary}</span>}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </Card>
          )}
        </>
      )}

      {data?.status === "ready" && data.redlineAvailable && acceptedCount > 0 && (
        <div
          className="sticky bottom-4 z-20 flex flex-wrap items-center gap-x-4 gap-y-2 rounded-lg border bg-background p-3 shadow-lg"
          data-testid="accepted-bar"
        >
          <span className="flex items-center gap-2 text-sm font-medium" role="status" aria-live="polite">
            <CheckCheck className="h-4 w-4 shrink-0 text-emerald-600" aria-hidden />
            {c.acceptedBar(acceptedCount)}
          </span>
          <div className="flex flex-wrap gap-2 sm:ml-auto">
            <Button size="sm" disabled={busy === "redline"} onClick={() => void download("redline")} data-testid="bar-redline">
              {busy === "redline" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Download className="mr-2 h-4 w-4" />}
              {c.downloadRedline}
            </Button>
            <Button size="sm" variant="outline" disabled={busy === "clean"} onClick={() => void download("clean")} data-testid="bar-clean">
              {busy === "clean" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <FileText className="mr-2 h-4 w-4" />}
              {c.cleanShort}
            </Button>
          </div>
        </div>
      )}

      <AlertDialog open={confirmRerun} onOpenChange={setConfirmRerun}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{c.rerun}</AlertDialogTitle>
            <AlertDialogDescription>{c.rerunConfirm}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{c.cancel}</AlertDialogCancel>
            <AlertDialogAction onClick={() => void rerun()}>{c.rerun}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ComparisonPage;
